import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDownRight } from 'lucide-react';

const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const Home = () => {
  const capabilities = [
    {
      title: 'Revenue & Sales',
      description: 'Strategic commercial frameworks that accelerate deal velocity and build sustainable pipeline architecture.',
      outcomes: ['Pipeline acceleration', 'Deal strategy', 'Partnership development']
    },
    {
      title: 'Creative Marketing',
      description: 'Brand positioning and campaign orchestration that captures attention and converts sophisticated audiences.',
      outcomes: ['Brand positioning', 'Campaign design', 'Market entry']
    },
    {
      title: 'Content & Media',
      description: 'Editorial systems and content infrastructure that establish thought leadership and drive organic growth.',
      outcomes: ['Content strategy', 'Production systems', 'Performance media']
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Discover',
      description: 'Deep immersion into your commercial landscape, competitive dynamics, and growth objectives.'
    },
    {
      number: '02',
      title: 'Build',
      description: 'Architect tailored strategies and assemble specialist teams aligned to your specific needs.'
    },
    {
      number: '03',
      title: 'Deploy',
      description: 'Execute with precision, measure rigorously, and iterate toward sustained momentum.'
    }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="min-h-screen flex items-center bg-black pt-36 md:pt-40"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.div 
              className="md:col-span-8"
              initial="hidden"
              animate="visible"
              variants={fadeUpVariant}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="text-sm uppercase tracking-[0.2em] text-gold font-bold mb-4 block">
                Confidential by design
              </span>
              <div className="mb-4">
                <img 
                  src="/logo-gold-trimmed.jpeg" 
                  alt="Novum Royale" 
                  className="h-40 md:h-52 lg:h-64 w-auto"
                />
              </div>
              <p className="mt-4 text-lg md:text-xl text-ivory/80 font-light leading-relaxed max-w-xl">
                An integrated commercial and creative growth partner for ambitious organisations seeking discretion, precision, and measurable outcomes.
              </p>
            </motion.div>
            
            <motion.div 
              className="md:col-span-4"
              initial="hidden"
              animate="visible"
              variants={fadeUpVariant}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex flex-col space-y-3">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="bg-gold text-black px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-goldhover transition-colors duration-300 text-center"
                >
                  Request an Introduction
                </Link>
                <a
                  href="#capabilities-deck"
                  data-testid="hero-cta-secondary"
                  className="border border-gold text-gold px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-gold hover:text-black transition-colors duration-300 text-center"
                >
                  Download Capabilities
                </a>
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            className="mt-12 flex items-center space-x-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <ArrowDownRight size={20} className="text-gold" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.2em] text-ivory/60 font-medium">
              Scroll to explore
            </span>
          </motion.div>
        </div>
      </section>

      {/* Credibility Section */}
      <section 
        data-testid="credibility-section"
        className="bg-ivory py-16 md:py-20 border-t border-softcharcoal/5"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              Who we are
            </span>
            <p className="font-heading text-2xl md:text-3xl lg:text-4xl text-charcoal leading-tight font-medium">
              A UK-based company orchestrating multi-disciplinary delivery across sales, marketing, and content. We operate at the intersection of commercial strategy and creative excellence.
            </p>
            <p className="mt-8 text-base md:text-lg text-softcharcoal/70 font-light leading-relaxed">
              Our approach combines senior strategic thinking with lean execution. We assemble specialist pods tailored to each engagement, ensuring every client receives focused attention without enterprise overhead.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section 
        data-testid="capabilities-section"
        className="bg-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              What we do
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-charcoal tracking-tight font-medium">
              Three Pillars of Growth
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {capabilities.map((capability, index) => (
              <motion.div
                key={capability.title}
                data-testid={`capability-${index + 1}`}
                className="group bg-white/50 backdrop-blur-sm border border-softcharcoal/5 p-8 md:p-10 hover:border-gold/30 transition-all duration-500"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUpVariant}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="text-sm text-gold font-semibold tracking-wider">
                    0{index + 1}
                  </span>
                  <ArrowRight size={18} className="text-softcharcoal/30 group-hover:text-gold group-hover:translate-x-1 transition-all duration-300" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-charcoal font-medium mb-4">
                  {capability.title}
                </h3>
                <p className="text-base md:text-lg text-softcharcoal/70 font-light leading-relaxed mb-6">
                  {capability.description}
                </p>
                <div className="pt-6 border-t border-softcharcoal/5">
                  <ul className="space-y-3">
                    {capability.outcomes.map((outcome) => (
                      <li key={outcome} className="text-sm md:text-base text-softcharcoal/60 font-medium flex items-center">
                        <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3"></span>
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="mt-12 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link
              to="/capabilities"
              data-testid="capabilities-link"
              className="inline-flex items-center space-x-3 text-sm uppercase tracking-widest font-medium text-charcoal hover:text-gold transition-colors duration-300"
            >
              <span>Explore our capabilities</span>
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Process Section - Dark */}
      <section 
        data-testid="process-section"
        className="bg-charcoal text-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
              Our approach
            </span>
            <h2 className="font-heading text-4xl md:text-5xl text-ivory tracking-tight font-medium">
              How We Work
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                data-testid={`process-step-${index + 1}`}
                className="relative"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeUpVariant}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px bg-ivory/10" />
                )}
                <span className="font-heading text-6xl md:text-7xl font-medium text-gold/20 block mb-4">
                  {step.number}
                </span>
                <h3 className="font-heading text-2xl md:text-3xl text-ivory font-medium mb-4">
                  {step.title}
                </h3>
                <p className="text-base text-ivory/60 font-light leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Selective Partnerships Section */}
      <section 
        data-testid="partnerships-section"
        className="bg-ivory py-16 md:py-20"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="md:col-span-7"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUpVariant}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-6 block">
                By invitation
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-charcoal tracking-tight font-medium mb-8">
                Selective Partnerships
              </h2>
              <p className="text-lg text-softcharcoal/80 font-light leading-relaxed mb-6">
                We work with a deliberately limited portfolio of clients, ensuring each engagement receives the senior attention it deserves. Most of our partnerships begin through referral.
              </p>
              <p className="text-base text-softcharcoal/60 font-light leading-relaxed">
                This selectivity allows us to maintain the quality and discretion our clients expect, while building relationships that extend beyond transactional engagements.
              </p>
            </motion.div>
            
            <motion.div 
              className="md:col-span-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeUpVariant}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-muted/50 p-8 md:p-12 border border-softcharcoal/5">
                <div className="space-y-8">
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-softcharcoal/50 font-semibold block mb-2">
                      Capacity
                    </span>
                    <span className="font-heading text-2xl text-charcoal">Limited availability</span>
                  </div>
                  <div className="h-px bg-softcharcoal/10"></div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-softcharcoal/50 font-semibold block mb-2">
                      Intake
                    </span>
                    <span className="font-heading text-2xl text-charcoal">By referral preferred</span>
                  </div>
                  <div className="h-px bg-softcharcoal/10"></div>
                  <div>
                    <span className="text-xs uppercase tracking-[0.2em] text-softcharcoal/50 font-semibold block mb-2">
                      Response
                    </span>
                    <span className="font-heading text-2xl text-charcoal">Within 48 hours</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section 
        data-testid="cta-section"
        className="text-ivory py-12 md:py-16"
        style={{ backgroundColor: '#000000' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h2 className="font-heading text-3xl md:text-4xl text-ivory font-medium mb-2">
                Ready to begin?
              </h2>
              <p className="text-ivory/60 font-light">
                Let's discuss how we can support your growth objectives.
              </p>
            </div>
            <div className="flex items-center space-x-8">
              <img 
                src="/logo-crest.jpeg" 
                alt="Novum Royale" 
                className="hidden md:block h-20 w-auto rounded-full"
              />
              <Link
                to="/contact"
                data-testid="cta-contact"
                className="bg-gold text-black px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-goldhover transition-colors duration-300"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Home;
